import Loading from '@/components/Loading';
import PosButton from '@/components/PosButton';
import PosPopover from '@/components/PosPopover';
import { MobileDisplayMode } from '@/components/User/Name';
import UserIntro from '@/components/User/UserIntro';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { Appointment, AppointmentEndpoint, getAppointmentStatusTag } from '@/models/appointment';
import { DateFormat, DateTimeFormat, TimeMinutesFormat } from '@/models/common';
import { User } from '@/models/user';
import { CalendarOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Flex, Grid, Row, Segmented, Space, Timeline, Typography } from 'antd';
import { ColProps, RowProps } from 'antd/es/grid';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRequest } from '../../api/hooks';
import { DatePicker } from '../DatePicker';

type AppointmentTimeline = {
  showCustomPicker?: boolean;
  colProps?: {
    left: ColProps;
    right: ColProps;
  };
  rowProps?: RowProps;

  selectMode?: boolean;
  onChange?: (appointment: Appointment | null) => void;
  onOpenDetail?: (appointment: Appointment | null) => void;
  filter?: (appointment: Appointment) => boolean;
};

const AppointmentTimeline = ({
  showCustomPicker = true,
  colProps = {
    left: { sm: 12, xs: 24 },
    right: { sm: 12, xs: 24 },
  },
  rowProps = { gutter: 12 },
  selectMode = false,
  onChange,
  onOpenDetail,
  filter = () => true,
}: AppointmentTimeline) => {
  const [, , helpers] = useContext(BaseInfoContext);
  const { xs } = Grid.useBreakpoint();
  const [now] = useState(dayjs());
  const [date, setDate] = useState(now);
  const { payload, loading, send } = useRequest<Appointment[]>(
    {
      url: `${AppointmentEndpoint}/timeline`,
      method: 'get',
    },
    { manual: true }
  );

  const reloadData = useCallback(() => {
    const start = date.startOf('day').format(DateTimeFormat);
    const end = date.endOf('day').format(DateTimeFormat);
    send({
      params: {
        start,
        end,
      },
    });
  }, [date]);

  useEffect(reloadData, [date]);

  const listData = (payload || []).filter(filter);
  const dataByTime = listData.reduce(
    (acc, cur) => {
      // const time = dayjs(cur.startTime).format(TimeMinutesFormat);
      const time = cur.startTime;
      acc[time] = acc[time] || [];
      acc[time].push(cur);
      return acc;
    },
    {} as { [startTime: string]: Appointment[] }
  );

  const segmentOption = useMemo(
    () => [
      {
        label: '昨天',
        value: now.subtract(1, 'day').format(DateFormat),
      },
      {
        label: '今天',
        value: now.format(DateFormat),
      },
      {
        label: '明天',
        value: now.add(1, 'day').format(DateFormat),
      },
      {
        label: '后天',
        value: now.add(2, 'day').format(DateFormat),
      },
    ],
    [now]
  );

  return (
    <Row {...rowProps}>
      <Col {...colProps.left} className="mb-m">
        <Flex align={xs ? 'center' : 'flex-start'} vertical gap={10}>
          <Segmented
            value={date.format(DateFormat)}
            size="large"
            onChange={value => {
              setDate(dayjs(value));
            }}
            vertical={!xs}
            options={segmentOption}
          />
          {showCustomPicker && (
            <DatePicker
              prefix={<>自定义日期</>}
              size="large"
              value={date}
              onChange={v => {
                setDate(v);
              }}
            />
          )}
        </Flex>
      </Col>
      <Col {...colProps.right}>
        <Loading className="text-center" busy={loading}>
          <Typography.Title level={3}>{date.format(DateFormat)}</Typography.Title>
          {listData.length === 0 ? <div className="mb-m">该日无有效预约记录</div> : null}
          {selectMode && (
            <div className={'mb-s'}>
              <PosButton
                type="secondary"
                icon={<ExclamationCircleOutlined />}
                onClick={() => {
                  onChange?.(null);
                }}>
                未预约客户
              </PosButton>
            </div>
          )}
          <Timeline
            mode="right"
            className="mt-l"
            items={Object.entries(dataByTime)
              .reduce((acc, [_, cur]) => {
                return acc.concat([], cur);
              }, [] as Appointment[])
              .map(item => {
                const color = now.isAfter(dayjs(item.startTime)) ? 'gray' : 'green';
                return {
                  key: item.id,
                  color: color,
                  label: (
                    <Space direction="horizontal" align="start">
                      {dayjs(item.startTime).format(TimeMinutesFormat)}
                      {selectMode && (
                        <PosButton
                          type="secondary"
                          onClick={() => {
                            onChange?.(item);
                          }}>
                          选择
                        </PosButton>
                      )}
                    </Space>
                  ),
                  children: (
                    <div key={item.id}>
                      <Space direction="vertical" size={5} align="start">
                        <div>
                          {getAppointmentStatusTag(item.status)} {item.count}人
                        </div>
                        {item.userId && (
                          <UserIntro
                            user={{ id: item.userId } as User}
                            showGender={false}
                            showFromUser={false}
                            showNickName={false}
                            showMobile={MobileDisplayMode.Short}
                            avatarProps={{ size: 24 }}
                            spaceProps={{ direction: 'horizontal', size: 3, wrap: true }}
                          />
                        )}
                      </Space>
                    </div>
                  ),
                };
              })}
          />
        </Loading>
      </Col>
    </Row>
  );
};

export default AppointmentTimeline;

export const AppointmentTimelinePopup = ({
  children,
  onChange,
  selectMode,
  filter,
  disabled = false,
}: { children: React.ReactNode; disabled?: boolean } & Pick<
  AppointmentTimeline,
  'onChange' | 'selectMode' | 'filter'
>) => {
  const [open, setOpen] = useState(false);

  return (
    <PosPopover
      open={open}
      onOpenChange={v => {
        setOpen(v);
      }}
      getPopupContainer={() => document.body}
      destroyOnHidden
      arrow={{ pointAtCenter: true }}
      content={
        <div style={{ minWidth: 450, minHeight: 160 }}>
          <AppointmentTimeline
            filter={filter}
            selectMode={selectMode}
            onOpenDetail={() => {
              setOpen(false);
            }}
            onChange={v => {
              if (selectMode) {
                setOpen(false);
              }
              onChange?.(v);
            }}
            showCustomPicker={false}
            rowProps={{
              align: 'top',
            }}
            colProps={{
              left: { span: 4 },
              right: { span: 20, style: { alignSelf: 'flex-start' } },
            }}
          />
        </div>
      }
      title={
        <>
          <CalendarOutlined /> 预约
        </>
      }
      trigger={disabled ? undefined : 'click'}>
      {children}
    </PosPopover>
  );
};
